import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import './index.css'
import App from './App.tsx'
import { initializePaddle } from './lib/paddleInit'

// Initialize PostHog (self-driving mode)
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    // Autocapture & pageviews
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    // Session replay
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
      maskTextSelector: '[data-ph-mask]',
    },
    // Heatmaps & dead clicks
    capture_heatmaps: true,
    capture_dead_clicks: true,
    // Performance & web vitals
    capture_performance: true,
  })
}

// Initialize Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: import.meta.env.PROD,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    beforeSend(event, hint) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const hasVercelFrame = frames.some((f) => {
        const fn = f.filename ?? '';
        return fn.includes('/_next-live/') || fn.includes('/_vercel/');
      });
      if (hasVercelFrame) return null;

      const url = (event.tags?.url as string) ?? '';
      if (url.includes('.vercel.app')) return null;

      const errMsg = hint?.originalException?.toString() ?? '';
      if (errMsg.includes('localhost:9876')) return null;

      // React 19 hydration errors are auto-recovered by React — suppress from Sentry
      const anyEvent = event as unknown as Record<string, unknown>;
      const occurrenceType = (anyEvent.occurrence as Record<string, unknown> | undefined)?.type;
      if (occurrenceType === 5003) return null;
      const originalException = hint?.originalException;
      if (originalException instanceof Error && originalException.message.includes('Hydration failed')) return null;

      // In-app browser errors (Instagram, Facebook, TikTok WebViews)
      const errValue = event.exception?.values?.[0]?.value ?? '';
      if (errValue.includes('webkit.messageHandlers') ||
          errValue.includes('postMessage') ||
          errValue.includes('Java exception') ||
          errValue.includes('Java object is gone') ||
          errValue.includes('Object Not Found Matching Id')) return null;

      return event;
    },
  });
}

// Catch unhandled errors from in-app browsers (Instagram/Facebook WebView)
// that would otherwise crash the React app
window.addEventListener('error', (e) => {
  if (e.message?.includes('webkit.messageHandlers') ||
      e.message?.includes('postMessage')) {
    e.preventDefault();
  }
});
window.addEventListener('unhandledrejection', (e) => {
  const msg = String(e.reason);
  if (msg.includes('webkit.messageHandlers') || msg.includes('postMessage')) {
    e.preventDefault();
  }
});

// Render the app immediately — do NOT block on Paddle init
const rootElement = document.getElementById('root')!;
const app = (
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);

// Only hydrate on routes that were actually pre-rendered; otherwise full render.
// Pre-rendered routes produce static HTML in dist/<route>/index.html — if the
// current path doesn't match one of those, the HTML inside #root belongs to a
// different page and hydration would fail with a mismatch error (#418).
const PRERENDERED_ROUTES = new Set([
  '/',
  '/precios',
  '/como-funciona',
  '/escandallo',
  '/food-cost',
  '/carta-digital-restaurante',
  '/software-gestion-restaurante',
  '/control-horario-hosteleria',
  '/plantilla-escandallo-excel',
  '/mermas-cocina',
  '/ingenieria-de-menu',
  '/ocr-facturas-restaurante',
  '/escanear-albaranes-restaurante',
  '/ocr-albaranes-hosteleria',
  '/digitalizar-facturas-restaurante',
  '/alergenos-restaurante',
  '/margen-beneficio-restaurante',
  '/alternativa-haddock',
  '/calculadora-food-cost',
  '/calculadora-escandallo',
  '/costeo-recetas-mexico',
  '/software-restaurantes-colombia',
  '/software-gastronomico-argentina',
  '/costeo-recetas-latinoamerica',
  '/desperdicio-alimentario-restaurante',
  '/software-escandallo',
  '/como-hacer-escandallo',
  '/escandallo-bebidas',
  '/ejemplo-escandallo',
  '/escandallo-panaderia',
  '/escandallo-catering',
  '/escandallo-pasteleria',
  '/calcular-precio-plato',
  '/escandallo-cocteleria',
  '/tabla-mermas-alimentos',
  '/rentabilidad-restaurante',
  '/control-costes-restaurante',
  '/gestion-compras-restaurante',
  '/appcc-restaurante',
  '/inspeccion-sanidad-restaurante',
  '/trazabilidad-alimentaria',
  '/calculadora-precio-venta',
  '/calculadora-margen-restaurante',
  '/pescado-de-temporada',
  '/calculadora-mermas',
  '/marisco-de-temporada',
  '/setas-de-temporada',
  '/plantilla-food-cost',
  '/calcular-coste-plato',
  '/ficha-tecnica-cocina',
  '/inventario-restaurante',
  '/ratio-food-cost-ideal',
  '/coste-materia-prima-restaurante',
  '/calculadora-ahorro-restaurante',
  '/productos-temporada/enero',
  '/productos-temporada/febrero',
  '/productos-temporada/marzo',
  '/productos-temporada/abril',
  '/productos-temporada/mayo',
  '/productos-temporada/junio',
  '/productos-temporada/julio',
  '/productos-temporada/agosto',
  '/productos-temporada/septiembre',
  '/productos-temporada/octubre',
  '/productos-temporada/noviembre',
  '/productos-temporada/diciembre',
  '/precios-mercado',
  '/calendario-temporada',
  '/indice-precios-mayoristas',
  '/embed-calculadora-food-cost',
  '/blog',
  '/blog/como-reducir-food-cost-restaurante',
  '/blog/errores-control-costes-restaurante',
  '/blog/inventario-restaurante-guia-completa',
  '/blog/food-cost-ideal-tipo-restaurante',
  '/blog/receta-estandar-restaurante',
]);

// Static routes + any /precios-mercado/* route (dynamic prerender)
const isPrerendered = PRERENDERED_ROUTES.has(window.location.pathname) ||
  window.location.pathname.startsWith('/precios-mercado');

const canHydrate = rootElement.hasChildNodes() && isPrerendered;

if (canHydrate) {
  hydrateRoot(rootElement, app);
} else {
  // Clear stale pre-rendered content from a different route before mounting
  if (rootElement.hasChildNodes()) {
    rootElement.innerHTML = '';
  }
  createRoot(rootElement).render(app);
}

// Initialize payment provider in the background after render
if (import.meta.env.VITE_PAYMENT_PROVIDER !== 'polar') {
  // Paddle: load and initialize Paddle.js
  window.__paddleReady = initializePaddle().catch(() => {
    // Paddle unavailable — checkout buttons will handle this gracefully
  });
}
// Polar: no global init needed — checkout is loaded on demand
